import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchNotificationSettingsThunk,
  saveNotificationSettingsThunk,
  selectNotificationsState,
  toggleNotificationSetting,
} from "../store/notificationsSlice";
import { selectCurrentUser } from "../../auth/store/authSlice";
import { isViewer } from "../../../shared/utils/roles";

export const useNotificationSettings = () => {
  const dispatch = useDispatch();
  const state = useSelector(selectNotificationsState);
  const currentUser = useSelector(selectCurrentUser);

  const canViewNotifications = useMemo(() => {
    return isViewer(currentUser?.user_role);
  }, [currentUser?.user_role]);

  useEffect(() => {
    dispatch(fetchNotificationSettingsThunk());
  }, [dispatch]);

  const isAnyBusy = state.isLoading || state.isSaving;
  const hasChanges = useMemo(
    () =>
      state.settings.delivery_failure_notification !==
        state.savedSettings.delivery_failure_notification ||
      state.settings.usage_alert_notification !==
        state.savedSettings.usage_alert_notification ||
      state.settings.system_alert_notification !==
        state.savedSettings.system_alert_notification,
    [state.savedSettings, state.settings],
  );

  const handleToggle = useCallback(
    (key) => {
      if (isAnyBusy || canViewNotifications) return;
      dispatch(toggleNotificationSetting(key));
    },
    [canViewNotifications, dispatch, isAnyBusy],
  );

  const handleSavePreferences = useCallback(async () => {
    if (!state.client || isAnyBusy || !hasChanges || canViewNotifications) return;
    await dispatch(saveNotificationSettingsThunk());
  }, [canViewNotifications, dispatch, hasChanges, isAnyBusy, state.client]);

  return {
    ...state,
    isAnyBusy,
    hasChanges,
    handleToggle,
    handleSavePreferences,
    canViewNotifications,
  };
};

export default useNotificationSettings;
